import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { getPipelinePropertyName, getPipelineStagePropertyName, allowsEmptyPipelineStage } from '../utils/pipelineUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { referenceToOptionWithReference } from '../../../utils/referenceLiteSearchSelectUtils';
import { useGetClosedateForDealObjects } from '../../../panels/hooks/useGetClosedateForDealObjects';
import { useAdditionalPropertyValues } from '../../../v2/hooks/useAdditionalPropertyValues';

const PipelineSelect = props => {
  const {
    clearable,
    dropdownFooter,
    inputRef,
    onChangeMultiple,
    placeholder,
    value
  } = props;
  const objectTypeId = useObjectTypeId();
  const httpClient = useHttpClient();
  const {
    loading: closedateLoading,
    getClosedateForDealObjects
  } = useGetClosedateForDealObjects({
    objectTypeId
  });
  const [pipelineId, pipelineStageId] = useAdditionalPropertyValues([getPipelinePropertyName(objectTypeId), getPipelineStagePropertyName(objectTypeId)]); // NOTE: we cannot use `usePropertyComponentResolver` because we need
  // `PIPELINE_WITH_PERMISSIONS` to ensure that PLP-hidden pipelines are disabled.
  // Making `PIPELINE_WITH_PERMISSIONS` the default for pipeline properties in
  // `getReferenceTypeFromProperty` (instead of `PIPELINE`) would break
  // filters/workflows by preventing users from selecting these pipelines

  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient
  }); // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).

  const handleSelectedOptionChange = useCallback(e => {
    const newPipelineId = `${e.target.value.value}`;

    if (newPipelineId === value) {
      // ignore repeat selection of the already-selected option
      // onChange does not be called when the user selects the already-
      // selected option, but `onSelectedOptionChange` does and we need
      // to manually ignore it
      return;
    }

    const reference = e.target.value.reference;
    const newStageId = reference && reference.additionalProperties && reference.additionalProperties.defaultPipelineStageId ? `${reference.additionalProperties.defaultPipelineStageId}` : null;
    const closedate = getClosedateForDealObjects({
      currentPipelineId: pipelineId,
      nextPipelineId: newPipelineId,
      currentStageId: pipelineStageId,
      nextStageId: newStageId
    });
    const changes = {
      [getPipelinePropertyName(objectTypeId)]: newPipelineId,
      [getPipelineStagePropertyName(objectTypeId)]: newStageId
    };

    if (closedate) {
      changes['closedate'] = closedate;
    }

    onChangeMultiple(changes);
  }, [getClosedateForDealObjects, objectTypeId, onChangeMultiple, pipelineId, pipelineStageId, value]);

  if (closedateLoading) {
    return null;
  }

  return /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, Object.assign({}, getTransferrableProps(props), {
    referenceToOption: referenceToOptionWithReference // NOTE: using onSelectedOptionChange instead of onChange; see above
    ,
    onChange: undefined,
    onSelectedOptionChange: handleSelectedOptionChange,
    resolver: pipelineResolver,
    ref: inputRef,
    anchorType: "button",
    value: value,
    clearable: clearable !== undefined ? clearable : allowsEmptyPipelineStage(objectTypeId),
    dropdownFooter: dropdownFooter,
    placeholder: placeholder || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPipelineV2.placeholder"
    })
  }));
};

PipelineSelect.displayName = 'PipelineSelect';
export default PipelineSelect;